// frontend/src/components/admin/UnidadeCard.jsx
import React, { useState } from 'react';
import { MapPin, Users, ChevronDown, ChevronUp, Home, User } from 'lucide-react';




const StatusBadge = ({ status }) => {
    const config = {
        aprovado: { 
            label: 'Aprovado', 
            color: 'bg-emerald-50 text-emerald-700 border-emerald-200' 
        },
        rejeitado: { 
            label: 'Rejeitado', 
            color: 'bg-rose-50 text-rose-700 border-rose-200' 
        },
        pendente: { 
            label: 'Pendente', 
            color: 'bg-amber-50 text-amber-700 border-amber-200' 
        },
    };

    const { label, color } = config[status] || config.pendente;

    return (
        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${color}`}>
            {label}
        </span>
    );
};

const UserRow = ({ user, type, onAction }) => {
    const isPending = user.status === 'pendente';
    const isProprietario = type === 'proprietario';

    return (
        <div className={`flex items-center justify-between py-3 px-3 rounded-lg transition-colors ${
            isProprietario 
                ? 'bg-[#449879]/5 hover:bg-[#449879]/10 border-l-2 border-[#449879]' 
                : 'hover:bg-gray-50/80'
        }`}>
            <div className="flex items-center gap-3 min-w-0">
                {/* Avatar con color según tipo */}
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-medium shrink-0 ${
                    isProprietario 
                        ? 'bg-[#449879] text-white' 
                        : 'bg-[#449879]/20 text-[#449879]'
                }`}>
                    {isProprietario ? (
                        <User className="w-4 h-4" />
                    ) : (
                        user.name.charAt(0).toUpperCase()
                    )}
                </div>
                <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                        <span className={`text-sm font-medium truncate ${
                            isProprietario ? 'text-[#1A2E2A]' : 'text-gray-700'
                        }`}>
                            {user.name}
                        </span>
                        <span className={`text-[10px] font-medium uppercase tracking-wider ${
                            isProprietario ? 'text-[#449879]' : 'text-gray-400'
                        }`}>
                            {isProprietario ? 'Proprietário' : 'Morador'}
                        </span>
                        <StatusBadge status={user.status} />
                    </div>
                    <div className="flex items-center gap-3 text-xs text-gray-400 mt-0.5">
                        <span className="truncate">{user.email}</span>
                        {user.phone && <span>• {user.phone}</span>}
                    </div>
                </div>
            </div>
            {isPending && (
                <div className="flex items-center gap-1.5 shrink-0">
                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            onAction(user.id, 'aprovado');
                        }}
                        className="px-3 py-1.5 text-xs font-medium text-[#449879] hover:bg-[#449879]/10 rounded-md transition-colors"
                    >
                        Aprovar
                    </button>
                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            onAction(user.id, 'rejeitado');
                        }}
                        className="px-3 py-1.5 text-xs font-medium text-rose-600 hover:bg-rose-50 rounded-md transition-colors"
                    >
                        Rejeitar
                    </button>
                </div>
            )}
        </div>
    );
};

export const UnidadeCard = ({ unidade, onUserAction }) => {
    const [expanded, setExpanded] = useState(true);

    const proprietario = unidade.usuarios?.find(u => u.tipo_residente === 'proprietario');
    const moradores = unidade.usuarios?.filter(u => u.tipo_residente === 'morador') || [];

    const todosLosUsuarios = unidade.usuarios || [];
    const hayPendientes = todosLosUsuarios.some(u => u.status === 'pendente');
    const todosAprovados = todosLosUsuarios.every(u => u.status === 'aprovado');

    const statusBadge = hayPendientes 
        ? 'pendente' 
        : (todosAprovados && todosLosUsuarios.length > 0 ? 'aprovado' : '');

    return (
        <div className="bg-white rounded-xl border border-gray-200/80 shadow-sm overflow-hidden transition-all hover:shadow-md hover:border-[#449879]/30">
            {/* Header */}
            <div 
                className="px-5 py-4 flex items-center justify-between cursor-pointer hover:bg-[#449879]/5 transition-colors"
                onClick={() => setExpanded(!expanded)}
            >
                <div className="flex items-center gap-4 min-w-0">
                    {/* Badge de número */}
                    <div className="w-9 h-9 rounded-lg bg-[#449879]/10 flex items-center justify-center shrink-0 border border-[#449879]/20">
                        <span className="text-[#449879] text-xs font-semibold">#{unidade.id}</span>
                    </div>
                    <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-sm font-semibold text-[#1A2E2A]">
                                {unidade.logradouro}, {unidade.numero}
                            </span>
                            <span className="text-xs text-gray-400">
                                • {unidade.bairro}
                            </span>
                        </div>
                        <div className="flex items-center gap-4 text-xs text-gray-400 mt-0.5">
                            <span className="flex items-center gap-1.5">
                                <MapPin className="w-3.5 h-3.5 text-[#449879]/70" />
                                {unidade.cep}
                            </span>
                            <span className="flex items-center gap-1.5">
                                <Users className="w-3.5 h-3.5 text-[#449879]/70" />
                                {unidade.usuarios?.length || 0}
                            </span>
                            {unidade.condominio && (
                                <span className="flex items-center gap-1.5">
                                    <Home className="w-3.5 h-3.5 text-[#449879]/70" />
                                    {unidade.condominio.nome}
                                </span>
                            )}
                        </div>
                    </div>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                    {statusBadge && <StatusBadge status={statusBadge} />}
                    {proprietario && (
                        <span className="hidden sm:block text-xs text-gray-500 truncate max-w-[120px]">
                            {proprietario.name}
                        </span>
                    )}
                    {expanded ? (
                        <ChevronUp className="w-4 h-4 text-gray-400 hover:text-[#449879] transition-colors" />
                    ) : (
                        <ChevronDown className="w-4 h-4 text-gray-400 hover:text-[#449879] transition-colors" />
                    )}
                </div>
            </div>

            {/* Body */}
            {expanded && (
                <div className="px-4 pb-4 pt-1 border-t border-[#449879]/10">
                    {proprietario && (
                        <UserRow 
                            user={proprietario}
                            type="proprietario"
                            onAction={onUserAction}
                        />
                    )}

                    {moradores.length > 0 && (
                        <>
                            <div className="pt-2 pb-1.5 px-3">
                                <span className="text-[10px] font-medium text-[#449879]/70 uppercase tracking-wider flex items-center gap-2">
                                    <span className="w-3 h-px bg-[#449879]/30"></span>
                                    Moradores • {moradores.length}
                                </span>
                            </div>
                            {moradores.map((morador) => (
                                <UserRow 
                                    key={morador.id}
                                    user={morador}
                                    type="morador"
                                    onAction={onUserAction}
                                />
                            ))}
                        </>
                    )}
                </div>
            )}
        </div>
    );
};

export default UnidadeCard;